/**
 * FieldWeatherCard — per-field weather display with spray status, GDD, 3-day forecast.
 *
 * Fetches GET /weather/fields (all-farm batch), filters to the given fieldId.
 * Extracted from DetailPage.jsx (Sprint 4 G.15) for reuse in WeatherTab.
 *
 * Props:
 *   fieldId — UUID string of the field
 *   token   — JWT auth token
 */
import { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';

const API = import.meta.env.VITE_API_URL ?? '';

const WMO_ICONS = {
  0: '\u2600\uFE0F', 1: '\uD83C\uDF24', 2: '\u26C5', 3: '\u2601\uFE0F',
  45: '\uD83C\uDF2B', 51: '\uD83C\uDF26', 53: '\uD83C\uDF26',
  61: '\uD83C\uDF27', 63: '\uD83C\uDF27', 65: '\u26C8',
  71: '\uD83C\uDF28', 80: '\uD83C\uDF26', 95: '\u26C8',
};

export default function FieldWeatherCard({ fieldId, token }) {
  const { t } = useTranslation();
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    if (!token || !fieldId) return;
    apiClient.get('/api/v1/aif/weather/fields').then(r => r.data).catch(() => null)
      .then(d => {
        const f = d?.fields?.find(fw => fw.field_id === fieldId);
        if (f && !f.error) setWeather(f);
      })
      .catch(() => {});
  }, [token, fieldId]);

  if (!weather) return null;

  const sprayColor = weather.spray_ok ? 'text-green-400' : 'text-red-400';
  const sprayBg = weather.spray_ok ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20';
  const weatherIcon = WMO_ICONS[weather.weather_code] || '\uD83C\uDF21';

  const gdd = weather.gdd;

  return (
    <div className="space-y-2" data-testid="field-weather-card">
      {/* Current conditions */}
      <div className={`rounded-xl border p-3 ${sprayBg}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg">{weatherIcon}</span>
            <div>
              <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
                {weather.temp_max_c != null ? `${weather.temp_max_c}\u00B0C` : '\u2014'}
              </span>
              {weather.temp_min_c != null && (
                <span className="text-xs text-[var(--aif-text-muted)] ml-1">/ {weather.temp_min_c}\u00B0C</span>
              )}
            </div>
            {weather.precipitation_mm > 0 && (
              <span className="text-xs text-blue-400">{'\uD83D\uDCA7'}{weather.precipitation_mm}mm</span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${weather.spray_ok ? (weather.spray_confidence === 'marginal' ? 'bg-amber-500' : 'bg-green-500') : 'bg-red-500'}`} />
            <span className={`text-xs font-medium ${sprayColor}`}>
              {weather.spray_ok ? t('weather.sprayOpen') : t('weather.sprayClosed')}
            </span>
          </div>
        </div>
        <p className="text-[11px] text-[var(--aif-text-muted)]">{weather.spray_reason}</p>
        {weather.spray_confidence === 'marginal' && (
          <p className="text-[10px] text-amber-400 mt-0.5">{'\u26A0\uFE0F'} {t('weather.sprayMarginal')}</p>
        )}
      </div>

      {/* GDD Progress */}
      {gdd && gdd.gdd_target > 0 && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-[var(--aif-text-secondary)]">{t('weather.gddTitle')}</span>
            <span className="text-xs text-[var(--aif-text-muted)]">{gdd.gdd_accumulated} / {gdd.gdd_target} GDD</span>
          </div>
          <div className="w-full h-2 bg-[var(--aif-bg-elevated)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--aif-color-primary)] rounded-full transition-all"
              style={{ width: `${Math.min(100, gdd.gdd_pct)}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-[var(--aif-text-muted)]">{gdd.gdd_pct}% {t('weather.gddPct')}</span>
            {gdd.estimated_harvest_date && (
              <span className="text-[10px] text-[var(--aif-color-primary)]">
                {'\uD83C\uDF3E'} ~{new Date(gdd.estimated_harvest_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
              </span>
            )}
          </div>
        </div>
      )}

      {/* 3-day mini forecast */}
      {weather.forecast_3d && weather.forecast_3d.length > 0 && (
        <div className="grid grid-cols-3 gap-1.5">
          {weather.forecast_3d.map(d => (
            <div key={d.date} className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-2 text-center">
              <div className="text-[10px] text-[var(--aif-text-muted)] mb-0.5">{d.day_name?.slice(0, 2)}</div>
              <div className="text-sm">{WMO_ICONS[d.weather_code] || '\uD83C\uDF21'}</div>
              <div className="text-[10px] text-[var(--aif-text-primary)]">{d.temp_max_c != null ? `${d.temp_max_c}\u00B0` : '\u2014'}</div>
              <div className={`w-1.5 h-1.5 rounded-full mx-auto mt-1 ${d.spray_ok ? 'bg-green-500' : 'bg-red-500'}`} title={d.spray_ok ? t('weather.sprayOpen') : t('weather.sprayClosed')} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
